/**
 * Driver-profile list widgets (#1805 slice A1).
 *
 * All three read the RECORD rather than the cell value: two of the columns are
 * `virtual` (no backing property at all — `effective.zones` and
 * `effective.daily_range` live one level down) and the third summarises an
 * object. That is exactly why they were `fields[].render` callbacks, and why
 * each carries `csvText: stringifyRaw(value)` — see
 * `features/stocktake/widgets.tsx` for the full argument.
 *
 * They translate through the i18n singleton, not `useTranslation`, because the
 * callbacks they replace did: `i18n.t` and the hook resolve against the same
 * live instance, but only the literal copy guarantees byte-identical markup,
 * which is what `__tests__/driverProfileResource.v2-parity.test.tsx` compares.
 */
import i18n from '@/i18n';
import { failHard } from '@resource-kit/core';
import { stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import type { DriverProfile } from './schema';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    driverEffectiveZones: { types: 'array'; modes: 'list' };
    driverEffectiveDailyRange: { types: 'json'; modes: 'list' };
    driverLearnedFrom: { types: 'json'; modes: 'list' };
  }
}

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The profile behind the cell. A record-shaped projection mounted without its
 * record is an authoring mistake — the callbacks these replace read
 * `row.effective` unguarded and threw on the same input.
 */
function profileOf(record: unknown, widget: string): DriverProfile {
  if (typeof record !== 'object' || record === null) {
    return failHard(`${widget}: mounted without a driver-profile record`);
  }
  return record as DriverProfile;
}

function formatWeight(weight: number): string {
  return weight.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

/** At most five zone chips; a driver with no learned zones says so in words. */
function DriverEffectiveZones({ record }: WidgetProps) {
  const row = profileOf(record, 'driverEffectiveZones');
  if (row.effective.zones === null || row.effective.zones.length === 0) {
    return (
      <span className="text-sm text-muted-foreground">{i18n.t('driverProfiles.noEffective')}</span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {row.effective.zones.slice(0, 5).map((zone) => (
        <span
          key={zone.zip}
          className="rounded-md border bg-muted/40 px-2 py-1 text-xs font-medium"
        >
          {i18n.t('driverProfiles.zoneWeight', {
            zip: zone.zip,
            weight: formatWeight(zone.weight),
          })}
        </span>
      ))}
    </div>
  );
}

function DriverEffectiveDailyRange({ record }: WidgetProps) {
  const row = profileOf(record, 'driverEffectiveDailyRange');
  return row.effective.daily_range === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="text-sm">
      {i18n.t('driverProfiles.dailyRangeValue', {
        p25: row.effective.daily_range.p25,
        median: row.effective.daily_range.median,
        p75: row.effective.daily_range.p75,
      })}
    </span>
  );
}

function DriverLearnedFrom({ record }: WidgetProps) {
  const row = profileOf(record, 'driverLearnedFrom');
  return row.learned_from === null ? (
    <span className="text-muted-foreground">—</span>
  ) : (
    <span className="text-sm text-muted-foreground">
      {i18n.t('driverProfiles.provenance', {
        count: row.learned_from.job_count,
        start: row.learned_from.window_start,
        end: row.learned_from.window_end,
      })}
    </span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerDriverProfileWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  registerWidget('driverEffectiveZones', {
    display: DriverEffectiveZones,
    csvText: rawCsv,
    supportedTypes: ['array'],
    modes: ['list'],
  });
  registerWidget('driverEffectiveDailyRange', {
    display: DriverEffectiveDailyRange,
    csvText: rawCsv,
    supportedTypes: ['json'],
    modes: ['list'],
  });
  registerWidget('driverLearnedFrom', {
    display: DriverLearnedFrom,
    csvText: rawCsv,
    supportedTypes: ['json'],
    modes: ['list'],
  });
}
